import useAxios from 'axios-hooks'
import React, { useContext, useEffect } from 'react'
import * as Icon from 'react-feather'
import {
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  useIntl,
} from 'react-intl'
import { useSelector } from 'react-redux'
import {
  DropdownMenu,
  DropdownToggle,
  Modal,
  Spinner,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from 'reactstrap'
import styled from 'styled-components'

import { ReactComponent as BoxIcon } from '~/assets/img/icons/box-icon.svg'
import { queries } from '~/constants/mediaQuery'
import { ProcessesContext } from '~/contexts/ProcessesContext'

const MANUAL_IMPORT_STEPS = {
  locations: 'locations',
  reviews: 'reviews',
  dailyMetrics: 'forceImport.step.dailyMetrics',
  keywords: 'forceImport.step.keywords',
  score: 'forceImport.step.score',
}

const ImportProgressButton = () => {
  const intl = useIntl()
  const userId = useSelector(state => state?.user?.profile?.id)
  const { processes, status, fetchProcesses } = useContext(ProcessesContext)
  const [modalOpen, setModalOpen] = React.useState(false)
  const [modalProcess, setModalProcess] = React.useState(null)
  const [locationData, setLocationData] = React.useState([])
  const [modalLoading, setModalLoading] = React.useState(false)
  const [, retryProcess] = useAxios(
    {
      method: 'POST',
      url: `/users/${userId}/processes/retry`,
    },
    { manual: true }
  )
  const [, ignoreProcess] = useAxios(
    {
      method: 'POST',
      url: `/users/${userId}/processes/ignore`,
    },
    { manual: true }
  )
  const [, fetchLocationExcerpts] = useAxios(
    {
      method: 'POST',
      url: `/locations/excerpt`,
    },
    { manual: true }
  )

  async function handleRetry(event, processId) {
    event.preventDefault()
    setModalOpen(false)
    setModalLoading(true)

    await retryProcess({
      url: `/users/${userId}/processes/${processId}/retry`,
    })
    await fetchProcesses()

    setModalProcess(null)
    setModalLoading(false)
  }

  function handleModal(event, process) {
    event.preventDefault()
    setModalProcess(process)
    setModalOpen(true)
  }

  function handleModalClose() {
    setModalProcess(null)
    setLocationData([])
    setModalLoading(false)
  }

  async function handleIgnore(event, processId) {
    event.preventDefault()
    setModalOpen(false)
    setModalLoading(true)

    await ignoreProcess({
      url: `/users/${userId}/processes/${processId}/ignore`,
    })
    await fetchProcesses()

    setModalProcess(null)
    setModalLoading(false)
  }

  useEffect(() => {
    if (modalProcess) {
      setModalLoading(true)
      const locationIds = modalProcess.errors?.errors?.map(
        error => error.locationId
      )
      fetchLocationExcerpts({
        data: {
          locationIds,
        },
      }).then(res => {
        setLocationData(res?.data?.locations)
        setModalLoading(false)
      })
    }
  }, [modalProcess])

  return (
    <StyledDropdown>
      <StyledModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        onClosed={handleModalClose}
      >
        <ModalDiv id="tooltip-modal-container">
          <div className="error-modal-content">
            <div className="error-modal-header">
              <div>
                <button className="circle-logo">
                  <Icon.ArrowLeft
                    color="#718096"
                    size="20"
                    onClick={() => setModalOpen(false)}
                  />
                </button>
                <div>
                  <FormattedMessage id="Locations with failed updates" />
                </div>
              </div>
              <div>
                <button className="x-button">
                  <Icon.X
                    color="#2D3748"
                    size="20"
                    onClick={() => setModalOpen(false)}
                  />
                </button>
              </div>
            </div>
            <hr />
            {!modalLoading && (
              <div className="error-modal-body">
                <div className="error-table-header">
                  <FormattedMessage id="Location" />
                </div>
                {modalProcess?.errors?.errors?.map(error => {
                  const location = locationData?.find(
                    loc => loc.id === error.locationId
                  )
                  const sanitizedId = location?.id?.split('/')[1]

                  return (
                    <div className="error-table-row" key={location?.id}>
                      <div>
                        <div className="error-name-row">
                          {location?.code && (
                            <div className="error-location-code">
                              {location.code}
                            </div>
                          )}
                          <div className="error-location-title">
                            {location?.name || (
                              <FormattedMessage id="Location not found" />
                            )}
                          </div>
                        </div>
                        <div
                          className="error-address-row"
                          id={`error-address-row-${sanitizedId}`}
                        >
                          {location ? (
                            <>
                              {location?.address?.addressLines[0]}
                              {', '}
                              {location?.address?.sublocality}
                              {' - '}
                              {location?.address?.locality}
                              {' ('}
                              {location?.address?.administrativeArea}
                              {')'}
                            </>
                          ) : (
                            `Location ID: ${error.locationId?.replace(
                              'locations/',
                              ''
                            )}`
                          )}
                        </div>
                        <UncontrolledTooltip
                          container="tooltip-modal-container"
                          target={`error-address-row-${sanitizedId}`}
                          placement="top"
                        >
                          {location?.address?.addressLines[0]}
                          {', '}
                          {location?.address?.sublocality}
                          {' - '}
                          {location?.address?.locality}
                          {' ('}
                          {location?.address?.administrativeArea}
                          {')'}
                        </UncontrolledTooltip>
                      </div>
                      <div>
                        <FormattedMessage id="Error" /> {error.id}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            {modalLoading && <Spinner />}
            <hr />
            <div className="error-modal-actions">
              <button
                className="error-modal-ignore"
                onClick={e => handleIgnore(e, modalProcess.id)}
                disabled={modalLoading}
              >
                <FormattedMessage id="Ignore Locations" />
              </button>
              <button
                className="error-modal-retry"
                onClick={e => handleRetry(e, modalProcess.id)}
                disabled={modalLoading}
              >
                <FormattedMessage id="Try Again" />
              </button>
            </div>
          </div>
        </ModalDiv>
      </StyledModal>
      <StyledToggle caret={false} tag="button" data-status={status}>
        <Icon.RefreshCcw size={24} color="rgba(47, 43, 61, 1)" />
        <div className="button-bubble"></div>
      </StyledToggle>
      <StyledMenu>
        <div className="menu-title">
          <FormattedMessage id="Import Progress" />
        </div>
        {!processes?.length && (
          <div className="menu-empty">
            <div>
              <BoxIcon />
            </div>
            <div>
              <FormattedMessage id="No imports in progress" />
            </div>
          </div>
        )}
        <div className="menu-list">
          {processes?.map(process => (
            <StyledProcessCard key={process.id}>
              <div className="process-card">
                <div className="process-head">
                  <div className="process-icon" data-status={process.status}>
                    {(process.status === 'RUNNING' ||
                      process.status === 'QUEUED') && (
                      <Icon.RefreshCcw size={18} />
                    )}
                    {process.status === 'COMPLETED' && <Icon.Check size={22} />}
                    {process.status === 'PARTIAL' && (
                      <Icon.AlertTriangle size={18} />
                    )}
                    {process.status === 'FAILED' && <Icon.X size={22} />}
                  </div>
                </div>
                <div className="process-body">
                  <div className="process-info">
                    <div className="process-info-title">
                      {(process.status === 'RUNNING' ||
                        process.status === 'QUEUED') && (
                        <FormattedMessage id="import.title.running" />
                      )}
                      {process.status === 'COMPLETED' && (
                        <FormattedMessage id="import.title.completed" />
                      )}
                      {process.status === 'PARTIAL' && (
                        <FormattedMessage id="import.title.partial" />
                      )}
                      {process.status === 'FAILED' && (
                        <FormattedMessage id="import.title.failed" />
                      )}
                    </div>
                    <div className="process-info-description">
                      {(process.status === 'RUNNING' ||
                        process.status === 'QUEUED') &&
                        (process.type === 'MANUAL_IMPORT' &&
                        process.input?.currentType ? (
                          <FormattedMessage
                            id="import.description.manualRunning"
                            values={{
                              type: intl.formatMessage({
                                id:
                                  MANUAL_IMPORT_STEPS[
                                    process.input.currentType
                                  ],
                              }),
                              project: process.input.projectName?.replace(
                                '.INTERNAL - ',
                                ''
                              ),
                            }}
                          />
                        ) : (
                          <FormattedMessage id="import.description.running" />
                        ))}
                      {process.status === 'COMPLETED' && (
                        <FormattedMessage id="import.description.completed" />
                      )}
                      {process.status === 'PARTIAL' && (
                        <FormattedMessage id="import.description.partial" />
                      )}
                      {process.status === 'FAILED' && (
                        <FormattedMessage id="import.description.failed" />
                      )}
                    </div>
                    {process.status === 'FAILED' ? null : (
                      <div className="process-info-progress">
                        <progress
                          className="process-info-progress-bar"
                          value={process.progress}
                          max={process.progressTotal}
                          data-status={process.status}
                        ></progress>
                        <div className="process-info-progress-text">
                          {process.progress}/{process.progressTotal}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="process-date">
                    <FormattedDate value={process.createdAt} />
                    {' - '}
                    <FormattedTime value={process.createdAt} />
                  </div>
                </div>
              </div>
              {(process.status === 'FAILED' ||
                process.status === 'PARTIAL') && (
                <div className="process-error" data-status={process.status}>
                  <div className="process-error-title">
                    <FormattedMessage id="import.fail.title" />
                  </div>
                  {process.status === 'PARTIAL' && (
                    <div className="process-error-description">
                      <FormattedMessage id="import.partial.description" />
                    </div>
                  )}
                  <div className="process-error-menu">
                    <button
                      className="process-error-retry"
                      onClick={e => handleRetry(e, process.id)}
                    >
                      <FormattedMessage id="import.fail.retry" />
                    </button>
                    {process.status === 'PARTIAL' &&
                      process.type !== 'MANUAL_IMPORT' && (
                        <button
                          className="process-error-details"
                          onClick={e => handleModal(e, process)}
                        >
                          <FormattedMessage id="import.fail.details" />
                        </button>
                      )}
                    {(process.status === 'FAILED' ||
                      (process.status === 'PARTIAL' &&
                        process.type === 'MANUAL_IMPORT')) && (
                      <button
                        className="process-error-details"
                        onClick={e => handleIgnore(e, process.id)}
                      >
                        <FormattedMessage id="import.fail.ignore" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </StyledProcessCard>
          ))}
        </div>
      </StyledMenu>
    </StyledDropdown>
  )
}

const StyledDropdown = styled(UncontrolledDropdown)`
  .dropdown-menu::before {
    display: none;
  }
`

const StyledToggle = styled(DropdownToggle)`
  position: relative;
  display: flex;
  width: 2rem;
  height: 2rem;
  justify-content: center;
  align-items: center;
  border-radius: 9999px;
  aspect-ratio: 1 / 1;
  border: none;

  @media ${queries.less.xl} {
    display: none;
  }

  @keyframes spin {
    from {
      transform: rotate(360deg);
    }
    to {
      transform: rotate(0deg);
    }
  }

  &[data-status='NONE'] {
    background-color: rgba(128, 131, 144, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.06);

    .button-bubble {
      display: none;
    }
  }

  &[data-status='PARTIAL'] {
    background-color: rgba(255, 159, 67, 0.16);
    border: 1px solid rgba(255, 159, 67, 0.16);

    .button-bubble {
      display: block;
      background-color: #ff9f43;
    }
  }

  &[data-status='RUNNING'],
  &[data-status='QUEUED'] {
    background-color: rgba(115, 103, 240, 0.16);
    border: 1px solid rgba(115, 103, 240, 0.16);

    svg {
      animation-name: spin;
      animation-duration: 2s;
      animation-timing-function: linear;
      animation-iteration-count: infinite;
    }

    .button-bubble {
      display: block;
      background-color: #7367f0;
    }
  }

  &:focus {
    outline: none;
  }

  .button-bubble {
    display: none;
    position: absolute;
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 9999px;
    aspect-ratio: 1 / 1;
    border: 1px solid #fff;
    top: 0;
    right: -0.20563rem;
  }
`

const StyledMenu = styled(DropdownMenu)`
  width: 23.75rem;
  display: flex;
  flex-direction: column;
  padding: 0;

  .menu-title {
    width: 100%;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid rgba(47, 43, 61, 0.12);
    color: rgba(47, 43, 61, 0.9);
    font-size: 0.9375rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1.375rem;
  }

  .menu-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 1.75rem;

    color: rgba(47, 43, 61, 0.9);
    text-align: center;
    font-size: 1.125rem;
    font-style: normal;
    font-weight: 600;
    line-height: 1.75rem;
  }

  .menu-list {
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }
`

const StyledProcessCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(47, 43, 61, 0.12);

  div {
    line-height: normal !important;
  }

  &:last-child {
    border-bottom: none;
  }

  .process-card {
    display: flex;
    gap: 0.75rem;

    .process-body {
      display: flex;
      width: 100%;
      flex-direction: column;
      gap: 0.5rem;

      .process-info {
        display: flex;
        width: 100%;
        flex-direction: column;
        gap: 0.25rem;

        .process-info-title {
          color: rgba(47, 43, 61, 0.9);
          font-size: 0.8125rem;
          font-style: normal;
          font-weight: 500;
          line-height: 1.25rem;
        }

        .process-info-description {
          color: rgba(47, 43, 61, 0.7);
          font-size: 0.8125rem;
          font-style: normal;
          font-weight: 400;
          line-height: 1.125rem;
          letter-spacing: 0.025rem;
        }

        .process-info-progress {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.5rem;

          .process-info-progress-bar {
            appearance: none;
            -webkit-appearance: none;
            height: 0.375rem;
            width: 100%;

            &::-webkit-progress-bar {
              border-radius: 9999px;
              background-color: rgba(47, 43, 61, 0.08);
            }

            &::-webkit-progress-value {
              border-radius: 9999px;
              transition: inline-size 2.5s ease-in-out;
            }

            &[data-status='RUNNING'],
            &[data-status='QUEUED'] {
              &::-webkit-progress-value {
                background-color: rgba(115, 103, 240, 1);
                box-shadow: 0 2px 6px 0 rgba(115, 103, 240, 0.3);
              }
            }

            &[data-status='COMPLETED'] {
              &::-webkit-progress-value {
                background-color: rgba(40, 199, 111, 1);
                box-shadow: 0 2px 6px 0 rgba(40, 199, 111, 0.3);
              }
            }

            &[data-status='PARTIAL'] {
              &::-webkit-progress-value {
                background-color: rgba(255, 159, 67, 1);
                box-shadow: 0 2px 6px 0 rgba(255, 159, 67, 0.3);
              }
            }

            &[data-status='FAILED'] {
              &::-webkit-progress-value {
                background-color: rgba(115, 103, 240, 1);
                box-shadow: 0 2px 6px 0 rgba(115, 103, 240, 0.3);
              }
            }

            &::-webkit-progress-inner-element {
              border-radius: 9999px;
            }
          }

          .process-info-progress-text {
            color: rgba(47, 43, 61, 0.7);
            font-size: 0.8125rem;
            font-style: normal;
            font-weight: 500;
            line-height: 1.25rem;
          }
        }
      }

      .process-date {
        color: rgba(47, 43, 61, 0.4);
        font-size: 0.8125rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.125rem;
        letter-spacing: 0.025rem;
      }
    }

    .process-icon {
      height: 2rem;
      width: 2rem;
      display: flex;
      justify-content: center;
      align-items: center;
      aspect-ratio: 1 / 1;
      border-radius: 0.375rem;

      &[data-status='RUNNING'],
      &[data-status='QUEUED'] {
        background-color: rgba(115, 103, 240, 0.16);
        color: rgba(115, 103, 240, 1);
      }

      &[data-status='COMPLETED'] {
        background-color: rgba(40, 199, 111, 0.08);
        color: rgba(36, 179, 100, 1);
      }

      &[data-status='PARTIAL'] {
        background-color: rgba(255, 159, 67, 0.16);
        color: rgba(255, 159, 67, 1);
      }

      &[data-status='FAILED'] {
        background-color: rgba(255, 76, 81, 0.16);
        color: rgba(230, 68, 73, 1);
      }
    }
  }

  .process-error {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: start;
    gap: 0.375rem;
    padding: 0.75rem;
    border-radius: 0.375rem;

    &[data-status='FAILED'] {
      background-color: rgba(255, 76, 81, 0.08);
    }

    &[data-status='PARTIAL'] {
      background-color: #fee3c9;
    }

    .process-error-title {
      color: rgba(47, 43, 61, 0.9);
      font-size: 0.8125rem;
      font-style: normal;
      font-weight: 500;
      line-height: 1.25rem;
    }

    .process-error-description {
      color: rgba(47, 43, 61, 0.7);
      font-size: 0.8125rem;
      font-style: normal;
      font-weight: 500;
      line-height: 1.25rem;
    }

    .process-error-menu {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 0.375rem;

      .process-error-retry,
      .process-error-details {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0.375rem 0.5625rem;
        border: none;
        margin: 0;
        background-color: transparent;

        font-size: 0.8125rem;
        font-style: normal;
        font-weight: 500;
        line-height: 1.125rem;
        text-transform: capitalize;

        &:focus {
          outline: none;
        }
      }

      .process-error-retry {
        color: rgba(115, 103, 240, 1);
      }

      .process-error-details {
        color: rgba(128, 131, 144, 1);
      }
    }
  }
`

const StyledModal = styled(Modal)`
  max-width: 50rem;
`

const ModalDiv = styled.div`
  .error-modal-content {
    display: flex;
    flex-direction: column;
    padding: 1.5rem;
    width: 100%;

    .spinner-border {
      align-self: center;
    }

    .error-modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      div:nth-child(1) {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        gap: 1rem;

        div {
          color: #171923;
          font-size: 1.25rem;
          font-weight: 700;
        }
      }

      .circle-logo {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #edf2f7;
        border-radius: 999999px;
        aspect-ratio: 1/1;
        border: none;

        &:hover {
          background-color: #e2e8f0;
        }

        &:focus {
          outline: none;
        }
      }

      .x-button {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: transparent;
        border: none;
        border-radius: 0.375rem;
        padding: 0.25rem;

        &:hover {
          background-color: rgba(0, 0, 0, 0.06);
        }

        &:focus {
          outline: none;
        }
      }
    }

    .error-modal-actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 0.9375rem;

      button {
        display: flex;
        justify-content: center;
        align-items: center;
        border: none;
        border-radius: 0.375rem;
        padding: 0.5rem 1.25rem;
        color: #fff;
        font-size: 0.9375rem;
        font-style: normal;
        font-weight: 500;
        line-height: 1.375rem;
        text-transform: capitalize;
        cursor: pointer;

        &:hover {
          opacity: 0.9;
        }

        &:focus {
          outline: none;
        }
      }

      .error-modal-ignore {
        background-color: rgba(255, 76, 81, 1);
        box-shadow: 0 2px 6px 0 rgba(255, 76, 81, 0.3);
      }

      .error-modal-retry {
        background-color: rgba(115, 103, 240, 1);
        box-shadow: 0 2px 6px 0 rgba(115, 103, 240, 0.3);
      }
    }

    .error-modal-body {
      display: flex;
      flex-direction: column;
      border: 1px solid rgba(0, 0, 0, 0.15);
      border-radius: 0.25rem;

      .error-table-header {
        display: flex;
        justify-content: start;
        align-items: center;
        padding: 0.75rem 1.5rem;
        background: #f9f9f9;
        border-bottom: 1px solid rgba(0, 0, 0, 0.15);
        border-radius: 0.25rem 0.25rem 0 0;

        color: rgba(47, 43, 61, 0.9);
        font-size: 0.8125rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.25rem;
      }

      .error-table-row {
        display: flex;
        justify-content: center;
        align-items: center;
        border-bottom: 1px solid rgba(0, 0, 0, 0.15);

        &:last-child {
          border-bottom: none;
          border-radius: 0 0 0.25rem 0.25rem;
        }

        > div {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 1rem 1.5rem;
          overflow: hidden;
        }

        > div:nth-child(1) {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          width: 70%;
          border-right: 1px solid rgba(0, 0, 0, 0.15);

          .error-name-row {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 0.5rem;

            .error-location-code {
              display: flex;
              justify-content: center;
              align-items: center;
              background-color: rgba(128, 131, 144, 0.08);
              border: 1px solid rgba(128, 131, 144, 0.16);
              border-radius: 0.25rem;
              padding: 0.25rem 0.75rem;
              color: #484848;
              font-size: 0.625rem;
              font-style: normal;
              font-weight: 600;
              line-height: 0.9375rem;
            }

            .error-location-title {
              color: rgba(47, 43, 61, 0.9);
              font-size: 0.8125rem;
              font-style: normal;
              font-weight: 600;
              line-height: 1.125rem;
              letter-spacing: 0.025rem;
            }
          }

          .error-address-row {
            display: block;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
            max-width: 100%;
            color: rgba(47, 43, 61, 0.7);
            font-size: 0.8125rem;
            font-style: normal;
            font-weight: 400;
            line-height: 1.125rem;
            letter-spacing: 0.025rem;
          }
        }

        > div:nth-child(2) {
          width: 30%;
          color: rgba(255, 76, 81, 1);
          font-size: 0.8125rem;
          font-style: normal;
          font-weight: 500;
          line-height: 1.125rem;
          text-transform: capitalize;
        }
      }
    }

    hr {
      width: 100%;
    }
  }
`

export default ImportProgressButton
